/* eslint-disable @typescript-eslint/naming-convention */

import { DashboardTab } from './CostType';

// ─────────────────────────────────────────────────────────────────────────────
// Virtual bucket Tenant strings — match CostService.cs constants exactly
// ─────────────────────────────────────────────────────────────────────────────

export const PLATFORM_BUCKET = '(Platform)';
export const SHARED_BUCKET = '(Shared)';
export const MANAGEMENT_BUCKET = '(Management)';
export const UNKNOWN_BUCKET = '(Unknown)';

// ─────────────────────────────────────────────────────────────────────────────
// Scope values — from BulkTagEngine v3 / TagRouter.cs
// ─────────────────────────────────────────────────────────────────────────────

export const SCOPES = [
  'Dedicated',
  'PlanShared',
  'FarmShared',
  'RegionalShared',
  'Platform',
  'Management',
  'Unknown',
] as const;

export type ScopeValue = (typeof SCOPES)[number];

/** Scopes that collapse into the "(Shared)" virtual bucket */
export const SHARED_SCOPES = new Set<string>(['FarmShared', 'RegionalShared']);

/** Scopes never attributed to a real tenant */
export const NON_TENANT_SCOPES = new Set<string>([
  'FarmShared',
  'RegionalShared',
  'Platform',
  'Management',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Layer values — from BulkTagEngine v3
// ─────────────────────────────────────────────────────────────────────────────

export const LAYERS = [
  'Hosting',
  'Data',
  'Monitoring',
  'Infrastructure',
  'Operations',
  'Security',
  'Network',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Recharts fill colours — one per scope
// ─────────────────────────────────────────────────────────────────────────────

export const SCOPE_COLORS: Record<string, string> = {
  Dedicated: '#3b82f6', // blue-500
  PlanShared: '#8b5cf6', // purple-500
  FarmShared: '#f59e0b', // amber-500
  RegionalShared: '#10b981', // emerald-500
  Platform: '#06b6d4', // cyan-500
  Management: '#6b7280', // gray-500
  Unknown: '#ef4444', // red-500
  // Virtual bucket aliases
  Shared: '#f59e0b',
};

// ─────────────────────────────────────────────────────────────────────────────
// MUI Chip colour tokens per scope — used by ScopeBadge
// ─────────────────────────────────────────────────────────────────────────────

export interface ScopeChipStyle {
  bg: string;
  color: string;
  border: string;
}

export const SCOPE_CHIP_STYLES: Record<string, ScopeChipStyle> = {
  Dedicated: {
    bg: 'rgba(59,130,246,0.15)',
    color: '#93c5fd',
    border: 'rgba(59,130,246,0.3)',
  },
  PlanShared: {
    bg: 'rgba(139,92,246,0.15)',
    color: '#c4b5fd',
    border: 'rgba(139,92,246,0.3)',
  },
  FarmShared: {
    bg: 'rgba(245,158,11,0.15)',
    color: '#fcd34d',
    border: 'rgba(245,158,11,0.3)',
  },
  RegionalShared: {
    bg: 'rgba(16,185,129,0.15)',
    color: '#6ee7b7',
    border: 'rgba(16,185,129,0.3)',
  },
  Platform: {
    bg: 'rgba(6,182,212,0.15)',
    color: '#67e8f9',
    border: 'rgba(6,182,212,0.3)',
  },
  Management: {
    bg: 'rgba(107,114,128,0.2)',
    color: '#d1d5db',
    border: 'rgba(107,114,128,0.3)',
  },
  Shared: {
    bg: 'rgba(245,158,11,0.15)',
    color: '#fcd34d',
    border: 'rgba(245,158,11,0.3)',
  },
  Unknown: {
    bg: 'rgba(239,68,68,0.15)',
    color: '#fca5a5',
    border: 'rgba(239,68,68,0.3)',
  },
};

export const FALLBACK_CHIP_STYLE: ScopeChipStyle = {
  bg: 'rgba(107,114,128,0.2)',
  color: '#d1d5db',
  border: 'rgba(107,114,128,0.3)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Trend chart — scopes to stack (order = bottom → top of stack)
// ─────────────────────────────────────────────────────────────────────────────

export const TREND_SCOPES = [
  'Dedicated',
  'PlanShared',
  'FarmShared',
  'RegionalShared',
  'Platform',
  'Management',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────────────

export interface TabDef {
  id: DashboardTab;
  label: string;
  badge?: number;
}

export const buildTabs = (orphanCount: number): TabDef[] => [
  { id: 'tenants', label: 'Tenants' },
  { id: 'scope', label: 'Scope' },
  { id: 'farms', label: 'Farms' },
  { id: 'servicetypes', label: 'Service Types' },
  { id: 'trend', label: 'Trend' },
  { id: 'orphans', label: 'Orphans', badge: orphanCount || undefined },
];
